import { useState, useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import axios from 'axios';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue } from
'../components/ui/select';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const interestOptions = [
{ value: 'real-estate', label: 'Real Estate & Construction' },
{ value: 'design', label: 'Architecture & Design' },
{ value: 'hospitality', label: 'Hospitality' },
{ value: 'materials', label: 'Building Materials' },
{ value: 'technology', label: 'Technology Solutions' },
{ value: 'partnership', label: 'Partnership Opportunities' },
{ value: 'careers', label: 'Careers' },
{ value: 'other', label: 'Other' }];


export const Contact = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    phone: '',
    interest: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus(null);

    try {
      await axios.post(`${API}/contact`, formData);
      setSubmitStatus('success');
      setFormData({
        name: '',
        email: '',
        company: '',
        phone: '',
        interest: '',
        message: ''
      });
    } catch (error) {
      setSubmitStatus('error');
      console.error('Contact form error:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section
      id="contact"
      data-testid="contact-section"
      ref={ref}
      className="py-24 lg:py-32 bg-[#F9F6F0]">

      <div className="max-w-[1400px] mx-auto px-6 lg:px-12">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 lg:gap-24">
          {/* Contact Form */}
          <div>
            <motion.p
              className="overline mb-4"
              initial={{ opacity: 0, y: 20 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.6 }}>

              Get in Touch
            </motion.p>

            <motion.h2
              data-testid="contact-title"
              className="font-heading text-3xl sm:text-4xl lg:text-5xl font-light tracking-tight text-[#1A1A1A] mb-8"
              initial={{ opacity: 0, y: 20 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.6, delay: 0.1 }}>

              Let's Build Together
            </motion.h2>

            <motion.form
              data-testid="contact-form"
              onSubmit={handleSubmit}
              className="space-y-6"
              initial={{ opacity: 0, y: 20 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.6, delay: 0.2 }}>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                <div>
                  <label className="text-xs uppercase tracking-widest text-[#4A4A4A] mb-2 block">
                    Name *
                  </label>
                  <Input
                    data-testid="contact-name-input"
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    required
                    className="rounded-none border-0 border-b border-black/15 bg-transparent px-0 focus-visible:ring-0 focus-visible:border-[#C5A880]"
                    placeholder="Your name" />

                </div>
                <div>
                  <label className="text-xs uppercase tracking-widest text-[#4A4A4A] mb-2 block">
                    Email *
                  </label>
                  <Input
                    data-testid="contact-email-input"
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    required
                    className="rounded-none border-0 border-b border-black/15 bg-transparent px-0 focus-visible:ring-0 focus-visible:border-[#C5A880]"
                    placeholder="your@email.com" />

                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                <div>
                  <label className="text-xs uppercase tracking-widest text-[#4A4A4A] mb-2 block">
                    Company
                  </label>
                  <Input
                    data-testid="contact-company-input"
                    type="text"
                    name="company"
                    value={formData.company}
                    onChange={handleInputChange}
                    className="rounded-none border-0 border-b border-black/15 bg-transparent px-0 focus-visible:ring-0 focus-visible:border-[#C5A880]"
                    placeholder="Your company" />

                </div>
                <div>
                  <label className="text-xs uppercase tracking-widest text-[#4A4A4A] mb-2 block">
                    Phone
                  </label>
                  <Input
                    data-testid="contact-phone-input"
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handleInputChange}
                    className="rounded-none border-0 border-b border-black/15 bg-transparent px-0 focus-visible:ring-0 focus-visible:border-[#C5A880]"
                    placeholder="+91" />

                </div>
              </div>

              <div>
                <label className="text-xs uppercase tracking-widest text-[#4A4A4A] mb-2 block">
                  Area of Interest *
                </label>
                <Select
                  value={formData.interest}
                  onValueChange={(value) => setFormData((prev) => ({ ...prev, interest: value }))}>

                  <SelectTrigger
                    data-testid="contact-interest-select"
                    className="rounded-none border-0 border-b border-black/15 bg-transparent px-0 focus:ring-0">

                    <SelectValue placeholder="Select your interest" />
                  </SelectTrigger>
                  <SelectContent className="rounded-none">
                    {interestOptions.map((option) =>
                    <SelectItem
                      key={option.value}
                      value={option.value}
                      data-testid={`interest-option-${option.value}`}>

                        {option.label}
                      </SelectItem>
                    )}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <label className="text-xs uppercase tracking-widest text-[#4A4A4A] mb-2 block">
                  Message *
                </label>
                <Textarea
                  data-testid="contact-message-input"
                  name="message"
                  value={formData.message}
                  onChange={handleInputChange}
                  required
                  rows={4}
                  className="rounded-none border-0 border-b border-black/15 bg-transparent px-0 resize-none focus-visible:ring-0 focus-visible:border-[#C5A880]"
                  placeholder="Tell us about your project or inquiry..." />

              </div>

              <button
                type="submit"
                data-testid="contact-submit-btn"
                disabled={isSubmitting || !formData.name || !formData.email || !formData.interest || !formData.message}
                className="btn-primary w-full sm:w-auto disabled:opacity-50 disabled:cursor-not-allowed">

                {isSubmitting ? 'Sending...' : 'Send Message'}
              </button>

              {submitStatus === 'success' &&
              <p data-testid="contact-success-message" className="text-sm text-green-700">
                  Thank you for your message. We'll be in touch soon.
                </p>
              }
              {submitStatus === 'error' &&
              <p data-testid="contact-error-message" className="text-sm text-red-700">
                  Something went wrong. Please try again.
                </p>
              }
            </motion.form>
          </div>

          {/* Founders' Note & Info */}
          <div>
            <motion.div
              className="bg-[#1A1A1A] p-8 lg:p-12 mb-8"
              initial={{ opacity: 0, y: 20 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.6, delay: 0.3 }}>

              <p className="overline mb-4 !text-[#C5A880]">Founders' Note</p>
              <h3 className="font-heading text-2xl lg:text-3xl font-light text-[#F9F6F0] mb-6">
                Why We Started This Journey
              </h3>
              <div className="text-sm text-[#F9F6F0]/80 leading-relaxed space-y-4">
                <p>
                  As brothers, we grew up watching our cities crumble under civic apathy. Broken roads, 
                  polluted rivers, unplanned construction, garbage on streets — and municipal corporations 
                  that seemed perpetually asleep at the wheel. We didn't just get frustrated. We got determined.
                </p>
                <p>
                  If the system won't build the cities we deserve, we decided we would build our own. 
                  Not gated communities that shut the world out, but open, breathing ecosystems where 
                  anyone can experience what modern, ecologically advanced living truly means.
                </p>
                <p>
                  Our vision is simple yet audacious: <span className="text-[#C5A880]">create self-sustaining townships</span> where 
                  services are at your fingertips, where nature isn't an afterthought but the foundation, 
                  where quality of life isn't a privilege but a standard.
                </p>
                <p>
                  This is not charity. This is nation-building through enterprise. We're putting our capital, 
                  our reputation, and our lives' work into creating what India's cities should have been.
                </p>
                <p className="text-[#F9F6F0] font-medium pt-2">
                  Welcome to Hemraj Grandsons. Welcome home.
                </p>
              </div>
              <div className="mt-6 pt-6 border-t border-[#F9F6F0]/10 flex justify-between items-end">
                <div>
                  <p className="text-[#F9F6F0] font-heading text-lg">Prashant Narula</p>
                  <p className="text-xs text-[#F9F6F0]/50 mt-1">Co-Founder</p>
                </div>
                <div className="text-right">
                  <p className="text-[#F9F6F0] font-heading text-lg">Shashank Narula</p>
                  <p className="text-xs text-[#F9F6F0]/50 mt-1">Co-Founder</p>
                </div>
              </div>
            </motion.div>

            {/* Contact Info */}
            <motion.div
              className="space-y-8"
              initial={{ opacity: 0, y: 20 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.6, delay: 0.4 }}>

              <div>
                <p className="overline mb-2">Headquarters</p>
                <p className="text-[#1A1A1A] font-light">
                  Hemraj Grandsons Pvt. Ltd.<br />
                  First Floor, 2A/96B,<br />
                  Shaheed Bhagat Singh Marg, NIT,<br />
                  Faridabad, Haryana - 121001
                </p>
              </div>

              <div>
                <p className="overline mb-2">Email</p>
                <a
                  href="mailto:info@hemrajgrandsons.com"
                  className="text-[#1A1A1A] font-light hover:text-[#C5A880] transition-colors">info@hemraj.one


                </a>
              </div>

              <div>
                <p className="overline mb-2">Working Hours</p>
                <p className="text-[#1A1A1A] font-light">
                  Monday - Saturday<br />
                  9:00 AM - 6:00 PM IST
                </p>
              </div>
            </motion.div>
          </div>
        </div>
      </div>
    </section>);

};